/**
 * The one banner above the shell (logic: packages/core logic/beta.ts `topBanner`): the connection banner, then
 * "Bundle is in beta", then "Get the app". Each banner says whether it wants the slot with `useTopBanner(id, wants)`
 * and renders only when that returns true. When the banner holding the slot stops wanting it (× / sent feedback /
 * download), the next one waits for the next session: a new tab or window (sessionStorage), not a reload.
 */
import { Beta, type TopBanner, type TopBannerWants } from "@bundle/core";
import { useEffect, useSyncExternalStore } from "react";

export type TopBannerId = keyof TopBannerWants;

const SESSION_KEY = "bundle.topBanner.closed";

function readClosed(): boolean {
  try {
    return sessionStorage.getItem(SESSION_KEY) === "1";
  } catch {
    return false;
  }
}

let wants: TopBannerWants = { offline: false, beta: false, getApp: false };
let closed = readClosed();
let shown: TopBanner = "none";
const listeners = new Set<() => void>();

function recompute(): void {
  const next = Beta.topBanner({ ...wants, dismissedThisSession: closed });
  if (next === shown) return;
  shown = next;
  for (const l of listeners) l();
}

/** `unmounting`: the banner left the screen (workspace switch), which is not a dismiss. */
export function setTopBannerWant(id: TopBannerId, on: boolean, unmounting = false): void {
  if (wants[id] === on) return;
  wants = { ...wants, [id]: on };
  if (!unmounting && Beta.closedThisSession({ shown, wants })) {
    closed = true;
    try {
      sessionStorage.setItem(SESSION_KEY, "1");
    } catch {
      // Private mode: this page load only.
    }
  }
  recompute();
}

function subscribe(l: () => void): () => void {
  listeners.add(l);
  return () => listeners.delete(l);
}

/** The banner holding the slot. */
export function topBannerShown(): TopBanner {
  return shown;
}

/** Says whether `id` wants the slot; true when it holds it. */
export function useTopBanner(id: TopBannerId, want: boolean): boolean {
  useEffect(() => setTopBannerWant(id, want), [id, want]);
  useEffect(() => () => setTopBannerWant(id, false, true), [id]);
  return useSyncExternalStore(subscribe, () => shown, () => shown) === id;
}

/** Test hook. */
export function resetTopBannerForTests(closedThisSession = false): void {
  wants = { offline: false, beta: false, getApp: false };
  closed = closedThisSession;
  shown = "none";
  try {
    sessionStorage.removeItem(SESSION_KEY);
  } catch {
    // ignore
  }
}
